import {
    type ProductIcon,
    LayersLightIcon as FeedStackIcon,
    LayersFilledIcon as FeedStackFilledIcon,
    HeartLightIcon as HeartIcon,
    HeartFilledIcon,
    HomeLightIcon as HomeIcon,
    HomeFilledIcon,
    BookHeartLightIcon as WishListIcon,
    BookHeartFilledIcon as WishListFilledIcon,
} from "@wags/ui/product-icons.tsx";
import {
    IconPeopleCircleLight,
    IconPeopleCircleFilled,
} from "@wags/ui/central-icons.tsx";

import { Link, useRouterState } from "@tanstack/react-router";
import { cn } from "@wags/ui/ui-utils";
import type { ComponentPropsWithoutRef } from "react";
import { activeBottomNavTab, type BottomNavTabId } from "@/lib/ui/bottomNav";
import { useChromeMatchSearch } from "@/lib/ui/last-match-search";
import type { MatchSearch } from "@/lib/ui/last-match-search.contract";

type Tab = {
    id: BottomNavTabId;
    label: string;
    to: string;
    search?: MatchSearch;
    Icon: ProductIcon;
    ActiveIcon: ProductIcon;
};

const TABS: readonly Tab[] = [
    {
        id: "find",
        label: "Find",
        to: "/find",
        Icon: HomeIcon,
        ActiveIcon: HomeFilledIcon,
    },
    {
        id: "feed",
        label: "Feed",

        to: "/discover",
        search: { source: "feed" },
        Icon: FeedStackIcon,
        ActiveIcon: FeedStackFilledIcon,
    },
    {
        id: "saved",
        label: "Saved",
        to: "/saved",
        Icon: HeartIcon,
        ActiveIcon: HeartFilledIcon,
    },
    {
        id: "match",
        label: "Match",
        to: "/match",
        Icon: WishListIcon,
        ActiveIcon: WishListFilledIcon,
    },
    {
        id: "profile",
        label: "Profile",
        to: "/profile",
        Icon: IconPeopleCircleLight,
        ActiveIcon: IconPeopleCircleFilled,
    },
] as const;

const bottomNavIconPressClass =
    "flex origin-center items-center justify-center transform-gpu transition-transform duration-200 ease-out group-active/nav:scale-94 group-active/nav:duration-150 group-active/nav:ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:group-active/nav:scale-100";

type BottomNavTone = "light" | "white";

const TONE_CLASS: Record<BottomNavTone, string> = {
    white: "border-doberman-black/8 bg-true-white after:bg-true-white",
    light: "border-doberman-black/8 bg-maltese-white after:bg-maltese-white",
};

export type BottomNavProps = ComponentPropsWithoutRef<"nav"> & {
    badges?: Partial<Record<BottomNavTabId, number>>;
};

export function BottomNav({ badges, ...props }: BottomNavProps) {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const matchSearch = useChromeMatchSearch();
    const activeTab = activeBottomNavTab(pathname);
    const tone: BottomNavTone =
        activeTab === "find" || activeTab === "feed" ? "white" : "light";

    return (
        <BottomNavBar aria-label="Primary navigation" tone={tone} {...props}>
            {TABS.map((tab) => (
                <BottomNavItem
                    key={tab.id}
                    to={tab.to}
                    search={tab.id === "match" ? matchSearch : tab.search}
                    label={tab.label}
                    Icon={tab.Icon}
                    ActiveIcon={tab.ActiveIcon}
                    isActive={activeTab === tab.id}
                    badge={badges?.[tab.id]}
                />
            ))}
        </BottomNavBar>
    );
}

export function BottomNavBar({
    tone = "light",
    className,
    children,
    ...props
}: ComponentPropsWithoutRef<"nav"> & { tone?: BottomNavTone }) {
    return (
        <nav
            data-slot="bottom-nav"
            data-safari-chrome-surface
            className={cn(
                "relative md:hidden rounded-none pb-[env(safe-area-inset-bottom)] after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-20",
                "border-t",
                TONE_CLASS[tone],
                className,
            )}
            {...props}
        >
            <ul className="relative mx-auto flex w-full max-w-md items-stretch justify-around">
                {children}
            </ul>
        </nav>
    );
}

export function BottomNavItem({
    to,
    search,
    label,
    Icon,
    ActiveIcon,
    isActive,
    badge = 0,
}: {
    to: string;
    search?: MatchSearch;
    label: string;
    Icon: ProductIcon;
    ActiveIcon: ProductIcon;
    isActive: boolean;
    badge?: number;
}) {
    const TabIcon = isActive ? ActiveIcon : Icon;
    return (
        <li className="flex-1">
            <Link
                to={to}
                search={search}
                activeOptions={{ exact: true }}
                aria-label={badge > 0 ? `${label}, ${badge} unread` : label}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                    "group/nav relative flex w-full touch-manipulation select-none flex-col items-center justify-center transition-colors [-webkit-tap-highlight-color:transparent] outline-none focus-visible:bg-doberman-black/[0.04]",
                    "h-14",
                    isActive
                        ? "text-doberman-black"
                        : "text-[color-mix(in_oklab,var(--doberman-black)_50%,var(--maltese-white))] hover:text-[color-mix(in_oklab,var(--doberman-black)_75%,var(--maltese-white))]",
                )}
            >
                <span className="relative flex h-9 w-14 items-center justify-center">
                    <span className={bottomNavIconPressClass}>
                        <TabIcon size={28} />
                    </span>

                    {badge > 0 && (
                        <span
                            aria-hidden="true"
                            className="absolute -top-0.5 right-2 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-shiba-orange px-1.25 text-[8px] font-semibold leading-none text-true-white"
                        >
                            {badge > 99 ? "99+" : badge}
                        </span>
                    )}
                </span>
            </Link>
        </li>
    );
}
